import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { FaBell, FaPlus, FaEdit, FaTrash, FaEye, FaCopy, FaSearch, FaHashtag } from 'react-icons/fa';
import { Card, CardHeader, CardBody, Button, StatusBadge } from '../components/Layout';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import PageShell from '../components/PageShell';
import { DataTable, DataTableHead } from '../components/DataTable';
import toast from 'react-hot-toast';
import { apiClient } from '../utils/apiClient';
const NotificationManagementPage = () => {
    const [activeTab, setActiveTab] = useState('templates');
    const [templates, setTemplates] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [platformFilter, setPlatformFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    // Modal states
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [deletingTemplate, setDeletingTemplate] = useState(false);
    const [previewVariables, setPreviewVariables] = useState({});
    const [previewContent, setPreviewContent] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    // Notification test form states
    const [notificationForm, setNotificationForm] = useState({
        title: '',
        body: '',
        type: 'system',
        priority: 'medium',
        platforms: [],
        template_id: '',
        recipients: '',
        expires_in: 24
    });
    const [isSending, setIsSending] = useState(false);
    // チャンネル一覧管理
    const [channels, setChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    useEffect(() => {
        loadData();
    }, [activeTab]);
    // チャンネル一覧取得
    const loadChannels = async () => {
        setChannelsLoading(true);
        try {
            const response = await apiClient.get('/api/v1/channels');
            let channelsData = [];
            if (response && typeof response === 'object') {
                const responseData = response;
                if (responseData.success && responseData.data && responseData.data.channels) {
                    channelsData = responseData.data.channels;
                }
                else if (responseData.channels) {
                    channelsData = responseData.channels;
                }
                else if (Array.isArray(responseData)) {
                    channelsData = responseData;
                }
            }
            setChannels(Array.isArray(channelsData) ? channelsData : []);
        }
        catch (err) {
            console.error('チャンネル取得エラー:', err);
            setChannels([]);
        }
        finally {
            setChannelsLoading(false);
        }
    };
    // テンプレートモーダルを開いた時にチャンネル一覧を読み込む
    useEffect(() => {
        if (showTemplateModal) {
            loadChannels();
        }
    }, [showTemplateModal]);
    const loadGoogleChatSpaces = async () => {
        try {
            setLoadingSpaces(true);
            const response = await apiClient.googleChatSpaces.getSpaces();
            if (response && typeof response === 'object') {
                const responseData = response;
                if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
                    setGoogleChatSpaces(responseData.data);
                }
                else if (Array.isArray(responseData)) {
                    setGoogleChatSpaces(responseData);
                }
                else if (responseData.data && Array.isArray(responseData.data)) {
                    setGoogleChatSpaces(responseData.data);
                }
                else {
                    console.warn('Unexpected spaces response structure:', responseData);
                    setGoogleChatSpaces([]);
                }
            }
            else {
                setGoogleChatSpaces([]);
            }
        }
        catch (err) {
            console.error('Failed to load Google Chat Spaces:', err);
            setGoogleChatSpaces([]);
        }
        finally {
            setLoadingSpaces(false);
        }
    };
    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            if (activeTab === 'templates') {
                await loadTemplates();
            }
            else if (activeTab === 'notifications') {
                await loadNotifications();
            }
        }
        catch (err) {
            console.error('loadData error:', err);
            const errorMessage = err instanceof Error ? err.message : 'データの読み込みに失敗しました';
            console.error('Setting error message:', errorMessage);
            setError(errorMessage);
        }
        finally {
            setLoading(false);
        }
    };
    const loadTemplates = async () => {
        try {
            const response = await apiClient.get('/api/v1/templates');
            // 🔒 安全なテンプレートデータ処理
            if (response && typeof response === 'object') {
                const responseData = response;
                let templateData = [];
                if (Array.isArray(responseData)) {
                    templateData = responseData;
                }
                else if (responseData.templates && Array.isArray(responseData.templates)) {
                    templateData = responseData.templates;
                }
                else if (responseData.data && Array.isArray(responseData.data)) {
                    templateData = responseData.data;
                }
                else if (responseData.success && responseData.data?.templates) {
                    templateData = responseData.data.templates;
                }
                else {
                    console.warn('テンプレートデータが配列形式ではありません:', response);
                    templateData = [];
                }
                setTemplates(templateData);
            }
            else {
                console.warn('テンプレートAPI応答が無効な形式です:', response);
                setTemplates([]);
            }
        }
        catch (error) {
            console.error('テンプレート読み込みエラー:', error);
            // エラー時はダミーテンプレートで継続
            setTemplates([
                {
                    id: 'default-system',
                    name: 'システム通知テンプレート',
                    description: 'デフォルトのシステム通知テンプレート',
                    content: '{{title}}: {{message}}',
                    platform: 'generic',
                    created_at: new Date().toISOString(),
                    type: 'notification',
                    format: 'text',
                    is_active: true,
                    is_default: true
                }
            ]);
        }
    };
    const loadNotifications = async () => {
        try {
            const response = await apiClient.get('/api/notifications');
            console.log('Notifications API Response:', response); // デバッグ用ログ
            // 🔒 安全な通知データ処理
            if (response && typeof response === 'object') {
                const responseData = response;
                let notificationData = [];
                if (Array.isArray(responseData)) {
                    notificationData = responseData;
                }
                else if (responseData.notifications && Array.isArray(responseData.notifications)) {
                    notificationData = responseData.notifications;
                }
                else if (responseData.data && Array.isArray(responseData.data)) {
                    notificationData = responseData.data;
                }
                else if (responseData.success && responseData.data?.notifications) {
                    notificationData = responseData.data.notifications;
                }
                else {
                    console.warn('通知データが配列形式ではありません:', response);
                    notificationData = [];
                }
                setNotifications(notificationData);
            }
            else {
                console.warn('通知API応答が無効な形式です:', response);
                setNotifications([]);
            }
        }
        catch (error) {
            console.error('通知読み込みエラー:', error);
            setNotifications([]);
        }
    };
    const handleCreateTemplate = () => {
        setSelectedTemplate(null);
        setTemplateForm({
            name: '',
            description: '',
            type: 'notification',
            platform: 'generic',
            format: 'text',
            subject: '',
            body: '',
            variables: [],
            space_id: '',
            channel_id: ''
        });
        setShowTemplateModal(true);
    };
    const handleEditTemplate = async (template) => {
        setSelectedTemplate(template);
        // テンプレート編集時に既存情報をフォームに設定
        const spaceId = template.metadata?.space_id || template.space_id || '';
        const channelId = template.metadata?.channel_id || template.channel_id || '';
        setTemplateForm({
            name: template.name || '',
            description: template.description || '',
            type: template.type || 'notification',
            platform: template.platform || 'generic',
            format: template.format || 'text',
            subject: template.subject || '',
            body: template.body || template.content || '',
            variables: template.variables?.map(v => v.name) || [],
            space_id: spaceId,
            channel_id: channelId
        });
        // Google Chatの場合、Space一覧を読み込む
        if (template.platform === 'google_chat') {
            await loadGoogleChatSpaces();
        }
        setShowTemplateModal(true);
    };
    const handleConfirmDeleteTemplate = async () => {
        if (!templateToDelete)
            return;
        setDeletingTemplate(true);
        try {
            const response = await apiClient.delete(`/api/v1/templates/${templateToDelete}`);
            if (response.success) {
                await loadTemplates();
                setTemplateToDelete(null);
            }
            else {
                throw new Error(response.error || 'テンプレートの削除に失敗しました');
            }
        }
        catch (err) {
            setError('テンプレートの削除に失敗しました');
        }
        finally {
            setDeletingTemplate(false);
        }
    };
    const handleDuplicateTemplate = async (templateId) => {
        try {
            const response = await apiClient.post(`/api/v1/templates/${templateId}/duplicate`);
            if (response.success) {
                await loadTemplates();
            }
            else {
                throw new Error(response.error || 'テンプレートの複製に失敗しました');
            }
        }
        catch (err) {
            setError('テンプレートの複製に失敗しました');
        }
    };
    const handlePreviewTemplate = async (templateId) => {
        try {
            // プレビュー用のサンプルデータ
            const previewData = {
                variables: {
                    // 基本システム変数
                    Title: "ワークフロー完了通知",
                    Message: "ワークフローが正常に完了しました",
                    // ワークフロー固有変数
                    workflow_name: "サンプルワークフロー",
                    processed_count: 1500,
                    execution_time: "3分45秒",
                    success_rate: "100%",
                    end_time: "2025-01-15T10:30:00Z",
                    workflow_url: "/etl-workflows?id=sample_workflow_123",
                    // ユーザー・テナント変数
                    user_name: "田中太郎",
                    tenant_name: "サンプル企業",
                    feature_count: 15,
                    // エラー関連変数（エラーテンプレート用）
                    error_message: "データ処理でエラーが発生しました",
                    failed_step: "データ変換ステップ",
                    error_time: "2025-01-15T10:25:00Z",
                    // 進行状況変数（進行テンプレート用）
                    current_step: "データ変換中",
                    progress_percent: "75%",
                    step_message: "データ変換処理を実行中です"
                },
                context: {
                    user_id: "sample_user",
                    tenant_id: "default",
                    platform: "generic",
                    language: "ja",
                    environment: "development"
                }
            };
            const response = await apiClient.post(`/api/v1/templates/${templateId}/preview`, previewData);
            if (response.success && response.data) {
                toast.success(`プレビューを生成しました: ${response.data.subject}`);
            }
            else {
                throw new Error(response.error || 'プレビューの生成に失敗しました');
            }
        }
        catch (err) {
            console.error('プレビューエラー:', err);
            setError('プレビューの生成に失敗しました');
        }
    };
    const filteredTemplates = templates.filter(template => {
        return ((searchQuery === '' ||
            template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            template.description.toLowerCase().includes(searchQuery.toLowerCase())) &&
            (platformFilter === '' || template.platform === platformFilter || template.platform === 'all') &&
            (typeFilter === '' || (template.type || 'notification') === typeFilter));
    });
    const filteredNotifications = (notifications || []).filter(notification => {
        return ((searchQuery === '' ||
            notification.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            notification.body?.toLowerCase().includes(searchQuery.toLowerCase())) &&
            (statusFilter === '' || notification.status === statusFilter) &&
            (platformFilter === '' || (notification.platforms || []).includes(platformFilter)) &&
            (typeFilter === '' || notification.type === typeFilter) &&
            (priorityFilter === '' || notification.priority === priorityFilter));
    });
    const getPlatformColor = (platform) => {
        const colors = {
            slack: 'bg-purple-100 text-purple-800',
            email: 'bg-blue-100 text-blue-800',
            mobile: 'bg-green-100 text-green-800',
            google_chat: 'bg-yellow-100 text-yellow-800',
            websocket: 'bg-gray-100 text-gray-800',
            all: 'bg-indigo-100 text-indigo-800',
        };
        return colors[platform] || 'bg-gray-100 text-gray-800';
    };
    const getStatusColor = (status) => {
        const colors = {
            sent: 'success',
            pending: 'warning',
            failed: 'error',
            cancelled: 'info',
        };
        return colors[status] || 'info';
    };
    // テンプレートモーダル状態管理
    const [templateForm, setTemplateForm] = useState({
        name: '',
        description: '',
        type: 'notification',
        platform: 'generic',
        format: 'text',
        subject: '',
        body: '',
        variables: [],
        space_id: '',
        channel_id: ''
    });
    const [googleChatSpaces, setGoogleChatSpaces] = useState([]);
    const [loadingSpaces, setLoadingSpaces] = useState(false);
    // プラットフォームがgoogle_chatの場合、Space一覧を読み込む
    useEffect(() => {
        if (templateForm.platform === 'google_chat' && showTemplateModal) {
            loadGoogleChatSpaces();
        }
    }, [templateForm.platform, showTemplateModal]);
    useEffect(() => {
        if (showNotificationModal) {
            loadGoogleChatSpaces();
        }
    }, [showNotificationModal]);
    // renderNotificationModal renders the notification test modal content
    const renderNotificationModal = () => {
        // テンプレート選択時の処理
        const handleTemplateChange = (templateId) => {
            if (!templateId) {
                // テンプレート選択解除
                setNotificationForm({
                    ...notificationForm,
                    template_id: '',
                    title: '',
                    body: '',
                    platforms: []
                });
                return;
            }
            // 選択されたテンプレートを取得
            const selectedTemplate = templates.find(t => t.id === templateId);
            if (selectedTemplate) {
                setNotificationForm({
                    ...notificationForm,
                    template_id: templateId,
                    title: selectedTemplate.subject || selectedTemplate.name || '',
                    body: selectedTemplate.body || selectedTemplate.content || '',
                    platforms: selectedTemplate.platform ? [selectedTemplate.platform] : []
                });
                // Google Chatテンプレートの場合、Space情報をMetadataに含める準備
                if (selectedTemplate.platform === 'google_chat' && selectedTemplate.space_id) {
                    // Space情報は後でMetadataに追加
                }
            }
        };
        // 全てのテンプレートを表示（Google ChatテンプレートはSpace情報も表示）
        const filteredTemplates = templates;
        return (_jsxs("div", { className: "space-y-4", children: [!notificationForm.template_id && (_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: ["\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8 ", _jsx("span", { className: "text-gray-500", children: "(\u30AA\u30D7\u30B7\u30E7\u30F3)" })] }), _jsxs("select", { value: notificationForm.template_id, onChange: (e) => handleTemplateChange(e.target.value), className: "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500", children: [_jsx("option", { value: "", children: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u3092\u9078\u629E\u3057\u3066\u304F\u3060\u3055\u3044" }), filteredTemplates.map((template) => (_jsxs("option", { value: template.id, children: [template.name, " (", template.platform, ")", template.space_name ? ` - ${template.space_name}` : ''] }, template.id)))] }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u3092\u9078\u629E\u3059\u308B\u3068\u3001\u30BF\u30A4\u30C8\u30EB\u30FB\u672C\u6587\u30FB\u30D7\u30E9\u30C3\u30C8\u30D5\u30A9\u30FC\u30E0\u304C\u81EA\u52D5\u5165\u529B\u3055\u308C\u307E\u3059" })] })), notificationForm.template_id && (_jsx("div", { className: "bg-indigo-50 border border-indigo-200 rounded-lg p-3", children: _jsxs("div", { children: [_jsxs("p", { className: "text-sm font-medium text-indigo-900", children: ["\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8: ", templates.find(t => t.id === notificationForm.template_id)?.name || '不明'] }), _jsx("p", { className: "text-xs text-indigo-700 mt-1", children: "\u3053\u306E\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u306E\u5185\u5BB9\u3067\u901A\u77E5\u30C6\u30B9\u30C8\u3092\u9001\u4FE1\u3057\u307E\u3059" })] }) })), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: ["\u30BF\u30A4\u30C8\u30EB ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "text", value: notificationForm.title, onChange: (e) => setNotificationForm({ ...notificationForm, title: e.target.value }), placeholder: "\u901A\u77E5\u306E\u30BF\u30A4\u30C8\u30EB\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044", className: "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: ["\u672C\u6587 ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("textarea", { value: notificationForm.body, onChange: (e) => setNotificationForm({ ...notificationForm, body: e.target.value }), placeholder: "\u901A\u77E5\u306E\u672C\u6587\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044", rows: 4, className: "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30BF\u30A4\u30D7" }), _jsxs("select", { value: notificationForm.type, onChange: (e) => setNotificationForm({ ...notificationForm, type: e.target.value }), className: "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500", children: [_jsx("option", { value: "system", children: "\u30B7\u30B9\u30C6\u30E0" }), _jsx("option", { value: "user", children: "\u30E6\u30FC\u30B6\u30FC" }), _jsx("option", { value: "alert", children: "\u30A2\u30E9\u30FC\u30C8" }), _jsx("option", { value: "custom", children: "\u30AB\u30B9\u30BF\u30E0" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u512A\u5148\u5EA6" }), _jsxs("select", { value: notificationForm.priority, onChange: (e) => setNotificationForm({ ...notificationForm, priority: e.target.value }), className: "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500", children: [_jsx("option", { value: "urgent", children: "\u7DCA\u6025" }), _jsx("option", { value: "high", children: "\u9AD8" }), _jsx("option", { value: "medium", children: "\u4E2D" }), _jsx("option", { value: "low", children: "\u4F4E" })] })] })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: ["\u914D\u4FE1\u30D7\u30E9\u30C3\u30C8\u30D5\u30A9\u30FC\u30E0 ", _jsx("span", { className: "text-red-500", children: "*" })] }), notificationForm.template_id ? (
                        // テンプレートが指定されている場合は読み取り専用表示
                        _jsx("div", { className: "text-sm text-gray-700", children: notificationForm.platforms.map((platform) => {
                                const platformLabels = {
                                    slack: 'Slack',
                                    email: 'Email',
                                    google_chat: 'Google Chat',
                                    websocket: 'WebSocket'
                                };
                                return platformLabels[platform] || platform;
                            }).join(', ') })) : (
                        // テンプレートが指定されていない場合は選択可能
                        _jsx("div", { className: "grid grid-cols-2 gap-2", children: [
                                { key: 'slack', label: 'Slack' },
                                { key: 'email', label: 'Email' },
                                { key: 'google_chat', label: 'Google Chat' },
                                { key: 'websocket', label: 'WebSocket' }
                            ].map(({ key, label }) => (_jsxs("label", { className: "flex items-center", children: [_jsx("input", { type: "checkbox", checked: notificationForm.platforms.includes(key), onChange: (e) => {
                                            if (e.target.checked) {
                                                setNotificationForm({
                                                    ...notificationForm,
                                                    platforms: [...notificationForm.platforms, key]
                                                });
                                            }
                                            else {
                                                setNotificationForm({
                                                    ...notificationForm,
                                                    platforms: notificationForm.platforms.filter(p => p !== key)
                                                });
                                            }
                                        }, className: "rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" }), _jsx("span", { className: "ml-2 text-sm text-gray-900", children: label })] }, key))) }))] })] }));
    };
    // 本文・件名から変数を自動抽出
    const extractVariables = (text) => {
        const pattern = /\{\{\.?(\w+)\}\}/g;
        const matches = text.matchAll(pattern);
        const variables = new Set();
        for (const match of matches) {
            variables.add(match[1]);
        }
        return Array.from(variables);
    };
    const renderTemplateModal = () => {
        const selectedChannel = channels.find(ch => ch.id === templateForm.channel_id);
        const hasChannels = channels.filter(ch => ch.status === 'active').length > 0;
        return (_jsxs("div", { className: "space-y-6", children: [!hasChannels && !channelsLoading && (_jsx("div", { className: "bg-yellow-50 border border-yellow-200 rounded-md p-4", children: _jsxs("div", { className: "flex", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx("svg", { className: "h-5 w-5 text-yellow-400", viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z", clipRule: "evenodd" }) }) }), _jsxs("div", { className: "ml-3", children: [_jsx("h3", { className: "text-sm font-medium text-yellow-800", children: "\u30C1\u30E3\u30F3\u30CD\u30EB\u304C\u4F5C\u6210\u3055\u308C\u3066\u3044\u307E\u305B\u3093" }), _jsx("div", { className: "mt-2 text-sm text-yellow-700", children: _jsx("p", { children: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u3092\u4F5C\u6210\u3059\u308B\u524D\u306B\u3001\u5148\u306B\u30C1\u30E3\u30F3\u30CD\u30EB\u7BA1\u7406\u3067\u30C1\u30E3\u30F3\u30CD\u30EB\u3092\u4F5C\u6210\u3057\u3066\u304F\u3060\u3055\u3044\u3002" }) })] })] }) })), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u540D" }), _jsx("input", { type: "text", value: templateForm.name, onChange: (e) => setTemplateForm({ ...templateForm, name: e.target.value }), placeholder: "\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u5B8C\u4E86\u901A\u77E5", className: "w-full px-3 py-2 border border-gray-300 rounded-lg" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30BF\u30A4\u30D7" }), _jsxs("select", { value: templateForm.type, onChange: (e) => setTemplateForm({ ...templateForm, type: e.target.value }), className: "w-full px-3 py-2 border border-gray-300 rounded-lg", children: [_jsx("option", { value: "notification", children: "\u901A\u77E5" }), _jsx("option", { value: "event", children: "\u30A4\u30D9\u30F3\u30C8" }), _jsx("option", { value: "alert", children: "\u30A2\u30E9\u30FC\u30C8" }), _jsx("option", { value: "custom", children: "\u30AB\u30B9\u30BF\u30E0" })] })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u8AAC\u660E" }), _jsx("textarea", { value: templateForm.description, onChange: (e) => setTemplateForm({ ...templateForm, description: e.target.value }), placeholder: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u306E\u8AAC\u660E\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044", rows: 2, className: "w-full px-3 py-2 border border-gray-300 rounded-lg" })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: ["\u9001\u4FE1\u5148\u30C1\u30E3\u30F3\u30CD\u30EB ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsxs("select", { value: templateForm.channel_id, onChange: (e) => {
                                const selectedChannel = channels.find(ch => ch.id === e.target.value);
                                // プラットフォームに応じたデフォルト形式を設定
                                let defaultFormat = 'text';
                                if (selectedChannel) {
                                    switch (selectedChannel.provider) {
                                        case 'slack':
                                        case 'discord':
                                            defaultFormat = 'markdown';
                                            break;
                                        case 'google_chat':
                                        case 'teams':
                                            defaultFormat = 'json';
                                            break;
                                        case 'websocket':
                                        case 'apns':
                                        case 'fcm':
                                            defaultFormat = 'json';
                                            break;
                                        case 'email':
                                        default:
                                            defaultFormat = 'text';
                                            break;
                                    }
                                }
                                setTemplateForm({
                                    ...templateForm,
                                    channel_id: e.target.value,
                                    // プラットフォームを自動設定
                                    platform: selectedChannel ? selectedChannel.provider : 'generic',
                                    // 形式を自動設定
                                    format: defaultFormat
                                });
                            }, className: "w-full px-3 py-2 border border-gray-300 rounded-lg", disabled: channelsLoading || !hasChannels, children: [_jsx("option", { value: "", children: "\u30C1\u30E3\u30F3\u30CD\u30EB\u3092\u9078\u629E\u3057\u3066\u304F\u3060\u3055\u3044" }), channels
                                    .filter(ch => ch.status === 'active')
                                    .map((channel) => (_jsxs("option", { value: channel.id, children: [channel.name, " (", channel.provider, ")"] }, channel.id)))] }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: channelsLoading
                                ? 'チャンネル一覧を読み込み中...'
                                : channels.length === 0
                                    ? 'チャンネルがありません。先にチャンネル管理で作成してください。'
                                    : `利用可能なチャンネル: ${channels.filter(ch => ch.status === 'active').length}件` })] }), selectedChannel && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30D7\u30E9\u30C3\u30C8\u30D5\u30A9\u30FC\u30E0" }), _jsx("input", { type: "text", value: selectedChannel.provider, disabled: true, className: "w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600" }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "\u9078\u629E\u3057\u305F\u30C1\u30E3\u30F3\u30CD\u30EB\u306E\u30D7\u30ED\u30D0\u30A4\u30C0\u30FC\u304C\u81EA\u52D5\u7684\u306B\u8A2D\u5B9A\u3055\u308C\u307E\u3059" })] })), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u4EF6\u540D" }), _jsx("input", { type: "text", value: templateForm.subject, onChange: (e) => {
                                const subject = e.target.value;
                                const bodyVars = extractVariables(templateForm.body);
                                const subjectVars = extractVariables(subject);
                                const allVars = Array.from(new Set([...subjectVars, ...bodyVars]));
                                setTemplateForm({
                                    ...templateForm,
                                    subject,
                                    variables: allVars
                                });
                            }, placeholder: "[TEICO] \u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u5B8C\u4E86 - {{.workflow_name}}", className: "w-full px-3 py-2 border border-gray-300 rounded-lg" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u672C\u6587" }), _jsx("textarea", { value: templateForm.body, onChange: (e) => {
                                const body = e.target.value;
                                const bodyVars = extractVariables(body);
                                const subjectVars = extractVariables(templateForm.subject);
                                const allVars = Array.from(new Set([...subjectVars, ...bodyVars]));
                                setTemplateForm({
                                    ...templateForm,
                                    body,
                                    variables: allVars
                                });
                            }, placeholder: "\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u300C{{.workflow_name}}\u300D\u304C\u6B63\u5E38\u306B\u5B8C\u4E86\u3057\u307E\u3057\u305F\u3002\n\n\u5B9F\u884C\u7D50\u679C:\n- \u51E6\u7406\u4EF6\u6570: {{.processed_count}}\u4EF6\n- \u5B9F\u884C\u6642\u9593: {{.execution_time}}", rows: 8, className: "w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm" })] }), templateForm.variables.length > 0 && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u691C\u51FA\u3055\u308C\u305F\u5909\u6570" }), _jsx("div", { className: "flex flex-wrap gap-2", children: templateForm.variables.map((varName) => (_jsx("span", { className: "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800", children: varName }, varName))) }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "\u672C\u6587\u3068\u4EF6\u540D\u304B\u3089\u81EA\u52D5\u7684\u306B\u62BD\u51FA\u3055\u308C\u307E\u3057\u305F" })] }))] }));
    };
    // handleSendNotification handles sending test notification
    const handleSendNotification = async () => {
        try {
            // バリデーション
            if (!notificationForm.title.trim()) {
                setError('タイトルは必須です');
                return;
            }
            if (!notificationForm.body.trim()) {
                setError('本文は必須です');
                return;
            }
            if (notificationForm.platforms.length === 0) {
                setError('配信プラットフォームを選択してください');
                return;
            }
            setIsSending(true);
            setError(null);
            // 選択されたテンプレートを取得（Space情報を取得するため）
            const selectedTemplate = notificationForm.template_id
                ? templates.find(t => t.id === notificationForm.template_id)
                : null;
            // Metadata構築（テンプレートのSpace情報を含める）
            const metadata = {
                source: 'manual_test',
                created_by: 'notification_management_ui'
            };
            // Google Chatテンプレートの場合、Space情報をMetadataに追加
            if (selectedTemplate?.platform === 'google_chat') {
                // テンプレートのspace_idを取得（直接フィールドまたはmetadataから）
                const templateSpaceId = selectedTemplate.space_id || selectedTemplate.metadata?.space_id;
                const templateSpaceName = selectedTemplate.space_name || selectedTemplate.metadata?.space_name;
                const templateSpaceType = selectedTemplate.metadata?.space_type;
                const templateWebhookUrl = selectedTemplate.metadata?.webhook_url;
                if (templateSpaceId) {
                    metadata.space_id = templateSpaceId;
                }
                if (templateSpaceName) {
                    metadata.space_name = templateSpaceName;
                }
                if (templateSpaceType) {
                    metadata.space_type = templateSpaceType;
                }
                if (templateWebhookUrl) {
                    metadata.webhook_url = templateWebhookUrl;
                }
                else if (templateSpaceId) {
                    // Metadataにwebhook_urlがない場合、Space管理APIから取得
                    // space_idの形式が 'space/AAQA3DQWtOw' の場合、'AAQA3DQWtOw' 部分で検索
                    const spaceIdToSearch = templateSpaceId.startsWith('space/')
                        ? templateSpaceId.replace('space/', '')
                        : templateSpaceId;
                    const selectedSpace = googleChatSpaces.find(s => {
                        // space_idが完全一致するか、'space/'プレフィックスを除いた部分が一致するか
                        return s.space_id === templateSpaceId ||
                            s.space_id === spaceIdToSearch ||
                            s.space_id.endsWith(spaceIdToSearch);
                    });
                    if (selectedSpace?.webhook_url) {
                        metadata.webhook_url = selectedSpace.webhook_url;
                    }
                    else {
                        console.warn('⚠️ Space管理APIからwebhook_urlを取得できませんでした:', {
                            templateSpaceId,
                            spaceIdToSearch,
                            availableSpaces: googleChatSpaces.map(s => s.space_id)
                        });
                    }
                }
                // デバッグ: Space情報の取得結果をログ出力
                console.log('🔍 Space情報取得結果:', {
                    templateSpaceId,
                    templateSpaceName,
                    templateSpaceType,
                    templateWebhookUrl,
                    metadataSpaceId: metadata.space_id,
                    metadataWebhookUrl: metadata.webhook_url,
                    googleChatSpacesCount: googleChatSpaces.length
                });
            }
            // 通知データ構築
            // 注意: template_idは送信しない（テンプレート選択はUI上での自動入力のみに使用）
            // Shibuki側でテンプレートが見つからない場合のエラーを避けるため
            const notificationData = {
                type: notificationForm.type,
                priority: notificationForm.priority,
                title: notificationForm.title,
                body: notificationForm.body, // Shibuki APIは 'body' フィールドを期待
                platforms: notificationForm.platforms,
                // template_idは送信しない（テンプレート選択はUI上での自動入力のみ）
                recipients: notificationForm.recipients ?
                    notificationForm.recipients.split(',').map(r => r.trim()).filter(r => r) :
                    // デフォルト受信者: 全体配信（文字列配列形式）
                    notificationForm.platforms.map(platform => platform === 'google_chat' ? 'spaces/general' :
                        platform === 'slack' ? '#general' :
                            platform === 'email' ? 'all@teico.platform' :
                                platform === 'websocket' ? 'all-users' : 'default'),
                expires_at: new Date(Date.now() + notificationForm.expires_in * 60 * 60 * 1000).toISOString(),
                metadata: metadata
            };
            // デバッグ: 通知データの内容をログ出力
            console.log('📤 通知送信データ:', JSON.stringify(notificationData, null, 2));
            console.log('📋 Metadata内容:', metadata);
            // Shibuki API経由で通知送信（Hishaku経由）
            const response = await apiClient.post('/api/v1/notifications', notificationData);
            // 成功時の処理
            setShowNotificationModal(false);
            // フォームリセット
            setNotificationForm({
                title: '',
                body: '',
                type: 'system',
                priority: 'medium',
                platforms: [],
                template_id: '',
                recipients: '',
                expires_in: 24
            });
            // 通知履歴を再読み込み
            if (activeTab === 'notifications') {
                await loadNotifications();
            }
            console.log('通知送信成功:', response);
        }
        catch (err) {
            console.error('通知送信エラー:', err);
            const errorMessage = err instanceof Error ? err.message : '通知の送信に失敗しました';
            setError(errorMessage);
        }
        finally {
            setIsSending(false);
        }
    };
    const handleSaveTemplate = async () => {
        try {
            // 入力検証
            if (!templateForm.name.trim()) {
                setError('テンプレート名は必須です');
                return;
            }
            if (!templateForm.channel_id) {
                setError('送信先チャンネルは必須です');
                return;
            }
            if (!templateForm.body.trim()) {
                setError('本文は必須です');
                return;
            }
            // variables を TemplateVariable 型に変換
            const variables = templateForm.variables.map(varName => ({
                name: varName,
                display_name: varName,
                description: `${varName} variable`,
                type: 'string',
                required: false,
                default_value: '',
                example: `example_${varName}`
            }));
            // Metadata: チャンネル情報を含める
            const metadata = {};
            // チャンネルIDが指定されている場合、チャンネル情報を追加
            if (templateForm.channel_id) {
                const selectedChannel = channels.find(ch => ch.id === templateForm.channel_id);
                if (selectedChannel) {
                    metadata.channel_id = templateForm.channel_id;
                    metadata.channel_name = selectedChannel.name;
                    metadata.channel_provider = selectedChannel.provider;
                }
            }
            if (templateForm.platform === 'google_chat' && templateForm.space_id) {
                const selectedSpace = googleChatSpaces.find(s => s.space_id === templateForm.space_id);
                if (selectedSpace) {
                    metadata.space_id = templateForm.space_id;
                    metadata.space_name = selectedSpace.space_name;
                    metadata.space_type = selectedSpace.space_type;
                    metadata.webhook_url = selectedSpace.webhook_url;
                }
            }
            const templateData = {
                Name: templateForm.name,
                Description: templateForm.description || '',
                Type: templateForm.type,
                Platform: templateForm.platform,
                Language: 'ja',
                Format: templateForm.format,
                Subject: templateForm.subject || '',
                Body: templateForm.body,
                Variables: variables,
                Metadata: metadata
            };
            if (selectedTemplate) {
                // 更新
                await apiClient.put(`/api/v1/templates/${selectedTemplate.id}`, templateData);
            }
            else {
                // 作成
                await apiClient.post('/api/v1/templates', templateData);
            }
            setShowTemplateModal(false);
            setSelectedTemplate(null);
            setTemplateForm({
                name: '',
                description: '',
                type: 'notification',
                platform: 'generic',
                format: 'text',
                subject: '',
                body: '',
                variables: [],
                space_id: '',
                channel_id: ''
            });
            // テンプレート一覧を再読み込み
            await loadTemplates();
        }
        catch (err) {
            console.error('テンプレート保存エラー:', err);
            setError('テンプレートの保存に失敗しました');
        }
    };
    const renderTemplatesTab = () => (_jsxs("div", { className: "space-y-6", children: [_jsx(Card, { children: _jsx(CardBody, { children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u691C\u7D22" }), _jsxs("div", { className: "relative", children: [_jsx(FaSearch, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" }), _jsx("input", { type: "text", placeholder: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u540D\u3001\u8AAC\u660E\u3067\u691C\u7D22...", className: "pl-10 w-full border rounded-md px-3 py-2", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30D7\u30E9\u30C3\u30C8\u30D5\u30A9\u30FC\u30E0" }), _jsxs("select", { className: "w-full border rounded-md px-3 py-2", value: platformFilter, onChange: (e) => setPlatformFilter(e.target.value), children: [_jsx("option", { value: "", children: "\u3059\u3079\u3066" }), _jsx("option", { value: "all", children: "All Platforms" }), _jsx("option", { value: "slack", children: "Slack" }), _jsx("option", { value: "email", children: "Email" }), _jsx("option", { value: "mobile", children: "Mobile" }), _jsx("option", { value: "google_chat", children: "Google Chat" }), _jsx("option", { value: "websocket", children: "WebSocket" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30BF\u30A4\u30D7" }), _jsxs("select", { className: "w-full border rounded-md px-3 py-2", value: typeFilter, onChange: (e) => setTypeFilter(e.target.value), children: [_jsx("option", { value: "", children: "\u3059\u3079\u3066" }), _jsx("option", { value: "notification", children: "\u901A\u77E5" }), _jsx("option", { value: "event", children: "\u30A4\u30D9\u30F3\u30C8" }), _jsx("option", { value: "alert", children: "\u30A2\u30E9\u30FC\u30C8" }), _jsx("option", { value: "custom", children: "\u30AB\u30B9\u30BF\u30E0" })] })] })] }) }) }), _jsx("div", { className: "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6", children: filteredTemplates.map((template) => (_jsxs(Card, { children: [_jsx(CardHeader, { title: template.name, subtitle: template.description, action: _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("span", { className: `px-2 py-1 rounded-full text-xs font-medium ${getPlatformColor(template.platform)}`, children: template.platform }), template.is_default && (_jsx("span", { className: "px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800", children: "\u30C7\u30D5\u30A9\u30EB\u30C8" }))] }) }), _jsx(CardBody, { children: _jsxs("div", { className: "space-y-3", children: [_jsxs("div", { children: [_jsxs("p", { className: "text-sm text-gray-600", children: ["\u30BF\u30A4\u30D7: ", template.type || 'notification'] }), template.metadata?.channel_id && (_jsxs("p", { className: "text-sm text-indigo-600 font-medium flex items-center mt-2", children: [_jsx(FaHashtag, { className: "mr-1" }), "\u30C1\u30E3\u30F3\u30CD\u30EB: ", template.metadata.channel_name || template.metadata.channel_id] })), template.platform === 'google_chat' && !template.metadata?.channel_id && (template.metadata?.space_name || template.space_name) && (_jsxs("p", { className: "text-sm text-indigo-600 font-medium", children: ["\uD83D\uDCCD Space: ", template.metadata?.space_name || template.space_name, template.metadata?.space_type && ` (${template.metadata.space_type})`] }))] }), _jsxs("div", { children: [_jsx("p", { className: "text-sm font-medium text-gray-700", children: "\u4EF6\u540D:" }), _jsx("p", { className: "text-sm text-gray-600 truncate", children: template.subject || '(なし)' })] }), _jsxs("div", { children: [_jsx("p", { className: "text-sm font-medium text-gray-700", children: "\u672C\u6587:" }), _jsx("p", { className: "text-sm text-gray-600 line-clamp-3", children: template.content || template.body || '(なし)' })] }), _jsx("div", { className: "flex items-center justify-end pt-2", children: _jsxs("div", { className: "flex items-center space-x-2 flex-wrap", children: [_jsx(Button, { size: "sm", variant: "primary", onClick: () => {
                                                        // テンプレート情報を設定してモーダルを開く
                                                        const selectedTemplate = templates.find(t => t.id === template.id);
                                                        if (selectedTemplate) {
                                                            setNotificationForm({
                                                                ...notificationForm,
                                                                template_id: template.id,
                                                                title: selectedTemplate.subject || selectedTemplate.name || '',
                                                                body: selectedTemplate.body || selectedTemplate.content || '',
                                                                platforms: selectedTemplate.platform ? [selectedTemplate.platform] : []
                                                            });
                                                            setShowNotificationModal(true);
                                                        }
                                                    }, icon: _jsx(FaBell, {}), children: "\u30C6\u30B9\u30C8" }), _jsx(Button, { size: "sm", variant: "secondary", onClick: () => {
                                                        console.log('Opening preview modal for template:', template.id);
                                                        // 他のモーダルを閉じる
                                                        setShowTemplateModal(false);
                                                        setShowNotificationModal(false);
                                                        setSelectedTemplate(template);
                                                        // 変数の初期値をセット（もしあれば）
                                                        const initialVariables = {};
                                                        if (template.variables) {
                                                            template.variables.forEach(v => {
                                                                if (v.default_value)
                                                                    initialVariables[v.name] = String(v.default_value);
                                                            });
                                                        }
                                                        setPreviewVariables(initialVariables);
                                                        setPreviewContent(null);
                                                        setShowPreviewModal(true);
                                                    }, icon: _jsx(FaEye, {}), children: "\u30D7\u30EC\u30D3\u30E5\u30FC" }), _jsx(Button, { size: "sm", variant: "secondary", onClick: () => handleDuplicateTemplate(template.id), icon: _jsx(FaCopy, {}), children: "\u8907\u88FD" }), _jsx(Button, { size: "sm", variant: "secondary", onClick: () => handleEditTemplate(template), icon: _jsx(FaEdit, {}), children: "\u7DE8\u96C6" }), _jsx(Button, { size: "sm", variant: "danger", onClick: () => setTemplateToDelete(template.id), icon: _jsx(FaTrash, {}), children: "\u524A\u9664" })] }) })] }) })] }, template.id))) }), filteredTemplates.length === 0 && (_jsxs("div", { className: "text-center py-12", children: [_jsx(FaBell, { className: "mx-auto h-12 w-12 text-gray-400" }), _jsx("h3", { className: "mt-2 text-sm font-medium text-gray-900", children: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u304C\u3042\u308A\u307E\u305B\u3093" }), _jsx("p", { className: "mt-1 text-sm text-gray-500", children: "\u65B0\u3057\u3044\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u3092\u4F5C\u6210\u3057\u3066\u59CB\u3081\u307E\u3057\u3087\u3046\u3002" }), _jsx("div", { className: "mt-6", children: _jsx(Button, { onClick: handleCreateTemplate, icon: _jsx(FaPlus, {}), children: "\u65B0\u898F\u4F5C\u6210" }) })] }))] }));
    // テンプレートプレビュー処理
    const handlePreview = async () => {
        if (!selectedTemplate)
            return;
        setPreviewLoading(true);
        try {
            const response = await apiClient.post(`/api/v1/templates/${selectedTemplate.id}/preview`, {
                variables: previewVariables
            });
            if (response.success && response.data) {
                setPreviewContent(response.data);
            }
            else {
                setPreviewContent(null);
                setError(response.error || 'プレビューの取得に失敗しました');
            }
        }
        catch (err) {
            console.error('Preview error:', err);
            setError('プレビューの生成に失敗しました');
        }
        finally {
            setPreviewLoading(false);
        }
    };
    // 自動プレビュー: モーダルを開いた時、または変数が変更された時に自動実行
    useEffect(() => {
        if (!showPreviewModal || !selectedTemplate) {
            return;
        }
        // 初回表示時は即座に実行
        const timeoutId = setTimeout(() => {
            handlePreview();
        }, showPreviewModal && !previewContent ? 0 : 500); // 初回は即座、その後の変更は500ms debounce
        return () => clearTimeout(timeoutId);
    }, [showPreviewModal, selectedTemplate, previewVariables]);
    const renderPreviewModal = () => {
        if (!showPreviewModal || !selectedTemplate)
            return null;
        const variables = selectedTemplate.variables || [];
        return (_jsx(Modal, { isOpen: true, onClose: () => {
                setShowPreviewModal(false);
                setPreviewContent(null);
                setPreviewVariables({});
            }, size: "lg", title: `テンプレートプレビュー: ${selectedTemplate.name}`, footer: _jsx(Button, { onClick: () => {
                    setShowPreviewModal(false);
                    setPreviewContent(null);
                    setPreviewVariables({});
                }, variant: "secondary", children: "\u9589\u3058\u308B" }), children: _jsxs("div", { className: "space-y-6", children: [variables.length > 0 && (_jsxs("div", { className: "bg-gray-50 p-4 rounded-lg", children: [_jsx("h4", { className: "text-sm font-medium text-gray-700 mb-3", children: "\u5909\u6570\u306E\u5165\u529B" }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: variables.map((variable) => (_jsxs("div", { children: [_jsxs("label", { className: "block text-xs font-medium text-gray-500 mb-1", children: [variable.display_name || variable.name, variable.required && _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "text", value: previewVariables[variable.name] || '', onChange: (e) => setPreviewVariables({
                                                ...previewVariables,
                                                [variable.name]: e.target.value
                                            }), placeholder: variable.example || '', className: "w-full px-3 py-2 border border-gray-300 rounded text-sm" })] }, variable.name))) })] })), previewLoading && (_jsx("div", { className: "flex justify-center items-center py-4", children: _jsx("div", { className: "text-sm text-gray-500", children: "\u30D7\u30EC\u30D3\u30E5\u30FC\u3092\u751F\u6210\u4E2D..." }) })), previewContent && (_jsxs("div", { className: "border border-gray-200 rounded-lg overflow-hidden", children: [_jsx("div", { className: "bg-gray-100 px-4 py-2 border-b border-gray-200 font-medium text-sm text-gray-700", children: "\u30D7\u30EC\u30D3\u30E5\u30FC\u7D50\u679C" }), _jsxs("div", { className: "p-4 space-y-4", children: [_jsxs("div", { children: [_jsx("div", { className: "text-xs text-gray-500 mb-1", children: "\u4EF6\u540D" }), _jsx("div", { className: "text-sm font-medium text-gray-900 bg-white p-2 border border-gray-200 rounded", children: previewContent.subject })] }), _jsxs("div", { children: [_jsx("div", { className: "text-xs text-gray-500 mb-1", children: "\u672C\u6587" }), _jsx("div", { className: "text-sm text-gray-800 bg-white p-3 border border-gray-200 rounded whitespace-pre-wrap", children: previewContent.body })] })] })] }))] }) }));
    };
    const renderNotificationsTab = () => (_jsxs("div", { className: "space-y-6", children: [_jsx(Card, { children: _jsx(CardBody, { children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u691C\u7D22" }), _jsxs("div", { className: "relative", children: [_jsx(FaSearch, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" }), _jsx("input", { type: "text", placeholder: "\u901A\u77E5\u30BF\u30A4\u30C8\u30EB\u3001\u672C\u6587\u3067\u691C\u7D22...", className: "pl-10 w-full border rounded-md px-3 py-2", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30D7\u30E9\u30C3\u30C8\u30D5\u30A9\u30FC\u30E0" }), _jsxs("select", { className: "w-full border rounded-md px-3 py-2", value: platformFilter, onChange: (e) => setPlatformFilter(e.target.value), children: [_jsx("option", { value: "", children: "\u3059\u3079\u3066" }), _jsx("option", { value: "slack", children: "Slack" }), _jsx("option", { value: "email", children: "Email" }), _jsx("option", { value: "mobile", children: "Mobile" }), _jsx("option", { value: "google_chat", children: "Google Chat" }), _jsx("option", { value: "websocket", children: "WebSocket" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30BF\u30A4\u30D7" }), _jsxs("select", { className: "w-full border rounded-md px-3 py-2", value: typeFilter, onChange: (e) => setTypeFilter(e.target.value), children: [_jsx("option", { value: "", children: "\u3059\u3079\u3066" }), _jsx("option", { value: "system", children: "\u30B7\u30B9\u30C6\u30E0" }), _jsx("option", { value: "user", children: "\u30E6\u30FC\u30B6\u30FC" }), _jsx("option", { value: "alert", children: "\u30A2\u30E9\u30FC\u30C8" }), _jsx("option", { value: "custom", children: "\u30AB\u30B9\u30BF\u30E0" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u512A\u5148\u5EA6" }), _jsxs("select", { className: "w-full border rounded-md px-3 py-2", value: priorityFilter, onChange: (e) => setPriorityFilter(e.target.value), children: [_jsx("option", { value: "", children: "\u3059\u3079\u3066" }), _jsx("option", { value: "high", children: "\u9AD8" }), _jsx("option", { value: "medium", children: "\u4E2D" }), _jsx("option", { value: "low", children: "\u4F4E" }), _jsx("option", { value: "urgent", children: "\u7DCA\u6025" })] })] })] }) }) }), _jsxs(DataTable, { children: [_jsx(DataTableHead, { children: _jsxs("tr", { children: [_jsx("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\u901A\u77E5" }), _jsx("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\u30BF\u30A4\u30D7\u30FB\u512A\u5148\u5EA6" }), _jsx("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\u30B9\u30C6\u30FC\u30BF\u30B9" }), _jsx("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\u30D7\u30E9\u30C3\u30C8\u30D5\u30A9\u30FC\u30E0" }), _jsx("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\u4F5C\u6210\u65E5\u6642" }), _jsx("th", { className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\u30A2\u30AF\u30B7\u30E7\u30F3" })] }) }), _jsx("tbody", { className: "bg-white divide-y divide-gray-200", children: (filteredNotifications || []).map((notification) => (_jsxs("tr", { children: [_jsx("td", { className: "px-6 py-4 whitespace-nowrap", children: _jsxs("div", { children: [_jsx("div", { className: "text-sm font-medium text-gray-900", children: notification.title }), _jsx("div", { className: "text-sm text-gray-500 truncate max-w-xs", children: notification.body })] }) }), _jsx("td", { className: "px-6 py-4 whitespace-nowrap", children: _jsxs("div", { className: "space-y-1", children: [_jsx("span", { className: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800", children: notification.type }), _jsx("span", { className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${notification.priority === 'high' ? 'bg-red-100 text-red-800' :
                                                    notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'}`, children: notification.priority })] }) }), _jsx("td", { className: "px-6 py-4 whitespace-nowrap", children: _jsx(StatusBadge, { status: getStatusColor(notification.status), text: notification.status }) }), _jsx("td", { className: "px-6 py-4 whitespace-nowrap", children: _jsx("div", { className: "flex flex-wrap gap-1", children: (notification.platforms || []).map((platform) => (_jsx("span", { className: `px-2 py-1 rounded-full text-xs font-medium ${getPlatformColor(platform)}`, children: platform }, platform))) }) }), _jsx("td", { className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500", children: notification.received_at ?
                                        new Date(notification.received_at).toLocaleString('ja-JP') :
                                        '日時不明' }), _jsx("td", { className: "px-6 py-4 whitespace-nowrap text-right text-sm font-medium", children: _jsx(Button, { size: "sm", variant: "secondary", onClick: () => setSelectedNotification(notification), children: "\u8A73\u7D30" }) })] }, notification.id))) })] }), filteredNotifications.length === 0 && (_jsxs("div", { className: "text-center py-12", children: [_jsx(FaBell, { className: "mx-auto h-12 w-12 text-gray-400" }), _jsx("h3", { className: "mt-2 text-sm font-medium text-gray-900", children: "\u901A\u77E5\u304C\u3042\u308A\u307E\u305B\u3093" }), _jsx("p", { className: "mt-1 text-sm text-gray-500", children: "\u307E\u3060\u901A\u77E5\u304C\u9001\u4FE1\u3055\u308C\u3066\u3044\u307E\u305B\u3093\u3002" })] }))] }));
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center h-64", children: _jsx("div", { className: "animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600" }) }));
    }
    return (_jsxs(PageShell, { title: "\u901A\u77E5", icon: _jsx(FaBell, { className: "text-indigo-600" }), actions: activeTab === 'templates' && (_jsxs("button", { type: "button", onClick: handleCreateTemplate, className: "inline-flex items-center justify-center rounded-md font-medium bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm", children: [_jsx(FaPlus, { className: "mr-2" }), "\u65B0\u898F\u4F5C\u6210"] })), toolbar: _jsx("div", { className: "border-b border-gray-200", children: _jsx("nav", { className: "-mb-px flex space-x-8", children: [
                    { key: 'templates', label: 'テンプレート', icon: FaEdit },
                    { key: 'notifications', label: '通知履歴', icon: FaBell },
                ].map(({ key, label, icon: Icon }) => (_jsxs("button", { onClick: () => setActiveTab(key), className: `${activeTab === key
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`, children: [_jsx(Icon, { className: "h-4 w-4" }), _jsx("span", { children: label })] }, key))) }) }), children: [error && (_jsx("div", { className: "mb-6 bg-red-50 border border-red-200 rounded-md p-4", children: _jsx("div", { className: "flex", children: _jsxs("div", { className: "ml-3", children: [_jsx("h3", { className: "text-sm font-medium text-red-800", children: "\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F" }), _jsx("div", { className: "mt-2 text-sm text-red-700", children: error })] }) }) })), activeTab === 'templates' && renderTemplatesTab(), activeTab === 'notifications' && renderNotificationsTab(), showTemplateModal && (_jsx(Modal, { isOpen: true, onClose: () => setShowTemplateModal(false), size: "xl", title: selectedTemplate ? 'テンプレート編集' : 'テンプレート作成', footer: _jsxs("div", { className: "flex items-center justify-end space-x-2", children: [_jsx(Button, { variant: "secondary", onClick: () => setShowTemplateModal(false), children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), _jsx(Button, { onClick: handleSaveTemplate, icon: _jsx(FaPlus, {}), disabled: !templateForm.channel_id || !templateForm.name.trim() || !templateForm.body.trim(), children: selectedTemplate ? '更新' : '作成' })] }), children: renderTemplateModal() })), renderPreviewModal(), showNotificationModal && (_jsx(Modal, { isOpen: true, onClose: () => {
                    setShowNotificationModal(false);
                    // モーダルを閉じる時にフォームをリセット
                    setNotificationForm({
                        title: '',
                        body: '',
                        type: 'system',
                        priority: 'medium',
                        platforms: [],
                        template_id: '',
                        recipients: '',
                        expires_in: 24
                    });
                }, size: "lg", title: "\u901A\u77E5\u30C6\u30B9\u30C8", footer: _jsxs("div", { className: "flex items-center justify-end space-x-2", children: [_jsx(Button, { variant: "secondary", onClick: () => {
                                setShowNotificationModal(false);
                                setNotificationForm({
                                    title: '',
                                    body: '',
                                    type: 'system',
                                    priority: 'medium',
                                    platforms: [],
                                    template_id: '',
                                    recipients: '',
                                    expires_in: 24
                                });
                            }, disabled: isSending, children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), _jsx(Button, { onClick: handleSendNotification, icon: _jsx(FaBell, {}), disabled: isSending, children: isSending ? '送信中...' : '通知送信' })] }), children: renderNotificationModal() })), _jsx(ConfirmDialog, { isOpen: !!templateToDelete, onClose: () => setTemplateToDelete(null), onConfirm: handleConfirmDeleteTemplate, danger: true, busy: deletingTemplate, title: "\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u524A\u9664\u306E\u78BA\u8A8D", message: "\u3053\u306E\u30C6\u30F3\u30D7\u30EC\u30FC\u30C8\u3092\u524A\u9664\u3057\u307E\u3059\u304B\uFF1F", confirmLabel: "\u524A\u9664" })] }));
};
export default NotificationManagementPage;
